import * as THREE from 'three'
import { gsap } from 'gsap'
import Experience from '../../Experience'

// crashIntoCamera で画面に衝突する進行度（この t でひび割れを発火し、以降ずり落ちる）
const IMPACT_T = 0.72

/**
 * UfoFlyby — サテライトをタップしたときの演出用 UFO。
 * 上空をゆらゆら波打ちながら横切る。待機中は非表示で、
 * launch() で画面外の左端から右端へ飛び、終わると再び隠れる。
 * 将来モデルを増やす場合はリソース名を変えてもう1機生成するだけでよい。
 */
export default class UfoFlyby {
  /**
   * @param {string} resourceName sources.js の gltfModel 名
   * @param {object} options
   *   size: 正規化後の最大辺（ユニット）
   *   modelYaw: 進行方向(+X)へ機体を向ける補正回転（rad）
   *   pathType: 'wave'（奥を一定深度で横断） | 'closeFlyby'（後半にカメラ前を掠めて抜ける）
   *            | 'crashIntoCamera'（制御不能でクルクル回転しながら画面に突撃 → ずり落ちる）
   *            | 'peekAtCamera'（途中でプレイヤーに気づき、目の前まで来て覗き込んでから飛び去る）
   *   spin: 機体の自転速度（rad/s。円盤UFO向け）
   *   duration: 演出全体の秒数
   *   onImpact: crashIntoCamera の衝突時コールバック。({ x, y } = 画面座標%) を受け取る
   *   soundCues: 進行度に紐づく効果音 [{ t, name, volume, pitch }]。各 launch() ごとに t 通過時へ1回再生
   */
  constructor(resourceName = 'ufoGreenSpaceship', { size = 1.2, modelYaw = 0, pathType = 'wave', spin = 0, duration = 9, onImpact = null, soundCues = [] } = {}) {
    this.experience = new Experience()
    this.scene = this.experience.scene
    this.camera = this.experience.camera
    this.quality = this.experience.quality

    this.size = size
    this.modelYaw = modelYaw
    this.pathType = pathType
    this.spin = spin
    this.onImpact = onImpact
    this.impactFired = false
    this.soundCues = soundCues
    this.firedCues = new Set()

    this.isFlying = false
    this.progress = { t: 0 }

    // 飛行パラメータ（debug GUI から調整可能）
    this.baseY = 3.2   // 飛行高度の基準
    this.depth = 12    // z 位置（星空より手前・惑星の奥）
    this.spanX = 14    // 横断の半幅（±spanX で画面外から画面外へ）
    this.waveAmp = 1.1 // 波の振幅
    this.duration = duration // 演出全体の秒数

    // 毎回の launch() でランダム化する波形（ゆらぎの個体差）
    this.waveFreq1 = 2.0
    this.waveFreq2 = 4.0
    this.wavePhase = 0

    // 使い回しのスクラッチ
    this.prevPosition = new THREE.Vector3()

    this.setModel(resourceName)
  }

  setModel(resourceName) {
    this.group = new THREE.Group()
    this.model = this.experience.resources.items[resourceName].scene

    // 原寸が不明なので最大辺を this.size ユニットに正規化し、モデル中心をグループ原点に合わせる
    const bounds = new THREE.Box3().setFromObject(this.model)
    const size = bounds.getSize(new THREE.Vector3())
    const modelScale = this.size / Math.max(size.x, size.y, size.z)
    this.model.scale.setScalar(modelScale)
    const center = bounds.getCenter(new THREE.Vector3())
    this.model.position.copy(center).multiplyScalar(-modelScale)

    // 前後の向きがあるモデルは進行方向（+X）へ向ける補正を掛ける
    // （position → rotation の順で適用されるよう、回転は中間グループに持たせる）
    this.modelPivot = new THREE.Group()
    this.modelPivot.rotation.y = this.modelYaw
    this.modelPivot.add(this.model)
    this.group.add(this.modelPivot)

    this.group.visible = false
    this.scene.add(this.group)
  }

  // 2つの sin を重ねた不規則な「ゆらゆら」の高さ成分
  getWaveY(t) {
    return this.baseY
      + Math.sin(t * Math.PI * 2 * this.waveFreq1 + this.wavePhase) * this.waveAmp
      + Math.sin(t * Math.PI * 2 * this.waveFreq2) * this.waveAmp * 0.45
  }

  // t(0..1) に対する飛行位置
  getPathPosition(t, target) {
    if (this.pathType === 'peekAtCamera') {
      // 前半は奥をゆらゆら → プレイヤーに気づいて減速しながらカメラ前 3.5 ユニットまで接近 →
      // ふわふわホバリングで観察 → 後ずさりしてから右上へ飛び去る
      const cruise = THREE.MathUtils.smoothstep(t, 0.0, 0.35)
      const approach = THREE.MathUtils.smoothstep(t, 0.35, 0.55)
      const retreat = THREE.MathUtils.smoothstep(t, 0.8, 1.0)

      // 観察中のふわふわボブ（接近完了〜離脱開始の間だけ）
      const peekWindow = THREE.MathUtils.smoothstep(t, 0.5, 0.6) * (1 - THREE.MathUtils.smoothstep(t, 0.78, 0.88))
      const bob = Math.sin(t * 60.0 + this.wavePhase) * 0.06 * peekWindow

      const farX = THREE.MathUtils.lerp(-this.spanX, -1.0, cruise)
      const x = THREE.MathUtils.lerp(farX, 0.45, approach) + retreat * retreat * 7.0
      const y = THREE.MathUtils.lerp(this.getWaveY(t), 0.15, approach) + bob + retreat * 1.2
      const z = THREE.MathUtils.lerp(this.depth, 26.5, approach) - retreat * 5.0
      return target.set(x, y, z)
    }

    if (this.pathType === 'crashIntoCamera') {
      // 前半は奥をゆらゆら → ジェット制御不能で迷走しながらカメラへ突撃 →
      // 画面（z=29 = カメラ1ユニット手前）に衝突して、張り付いたまま画面下へずり落ちる
      const cruise = THREE.MathUtils.smoothstep(t, 0.0, 0.45)
      const dive = THREE.MathUtils.smoothstep(t, 0.45, IMPACT_T)
      const fall = THREE.MathUtils.smoothstep(t, IMPACT_T, 1.0)

      // 迷走の揺れ（突撃の中盤で最大、衝突時にはゼロへ収束させて衝突位置を画面中央付近に確定させる）
      const wobble = Math.sin(t * 40.0 + this.wavePhase) * 0.6 * Math.sin(dive * Math.PI)

      const farX = THREE.MathUtils.lerp(-this.spanX, -1.5, cruise)
      const x = THREE.MathUtils.lerp(farX, 0.15, dive * dive) + wobble * 0.5
      const y = THREE.MathUtils.lerp(this.getWaveY(t), 0.0, dive) + wobble - fall * fall * 1.6
      const z = THREE.MathUtils.lerp(this.depth, 29.0, dive * dive) // 衝突後は張り付いたまま
      return target.set(x, y, z)
    }

    if (this.pathType === 'closeFlyby') {
      // 前半は奥をゆらゆら横断し、後半はカメラ(0,0,30)の右横スレスレを掠めて抜ける。
      // 最接近点（x≈1, z≈27.8 = カメラ2.2ユニット手前）を明示的に通してから一気に離脱する
      const cruise = THREE.MathUtils.smoothstep(t, 0.0, 0.55) // 左端 → 中央
      const swoop = THREE.MathUtils.smoothstep(t, 0.5, 1.0)   // 接近フェーズの進行
      const pass = THREE.MathUtils.smoothstep(swoop, 0.0, 0.85) // 最接近点までの接近
      const exit = THREE.MathUtils.smoothstep(swoop, 0.85, 1.0) // 通過後の離脱

      const farX = THREE.MathUtils.lerp(-this.spanX, 0.5, cruise)
      const x = THREE.MathUtils.lerp(farX, 1.0, pass) + exit * 4.0
      const y = THREE.MathUtils.lerp(this.getWaveY(t), -0.3, pass) - exit * 0.5
      const z = THREE.MathUtils.lerp(this.depth, 27.8, pass) + exit * 1.4
      return target.set(x, y, z)
    }

    // wave: 奥を一定深度で横断。終盤ほど効く下降を添える
    const x = -this.spanX + t * this.spanX * 2
    const descent = t * t * 2.2
    return target.set(x, this.getWaveY(t) - descent, this.depth)
  }

  // 機体の現在位置をスクリーン座標（% 単位）に投影する
  getScreenPosition() {
    const projected = this.prevPosition.copy(this.group.position).project(this.camera.instance)
    return {
      x: (projected.x * 0.5 + 0.5) * 100,
      y: (-projected.y * 0.5 + 0.5) * 100,
    }
  }

  launch() {
    if (this.isFlying) return
    this.isFlying = true

    this.waveFreq1 = 1.6 + Math.random() * 0.8
    this.waveFreq2 = 3.2 + Math.random() * 1.6
    this.wavePhase = Math.random() * Math.PI * 2

    this.progress.t = 0
    this.impactFired = false
    this.firedCues.clear()
    this.getPathPosition(0, this.group.position)
    this.group.visible = true

    // reducedMotion 中は一瞬で終わらせる（実質スキップ）
    const duration = this.quality.reducedMotion
      ? 0.01
      : Math.max(this.duration * this.quality.motionScale, 0.01)

    gsap.to(this.progress, {
      t: 1,
      duration,
      ease: 'none',
      onUpdate: () => {
        this.prevPosition.copy(this.group.position)
        this.getPathPosition(this.progress.t, this.group.position)

        // 進行度に紐づく効果音を1回ずつ再生（reducedMotion 中は飛行が一瞬なので鳴らさない）
        if (!this.quality.reducedMotion) {
          for (const cue of this.soundCues) {
            if (this.progress.t >= cue.t && !this.firedCues.has(cue)) {
              this.firedCues.add(cue)
              this.experience.audioManager?.playSE(cue.name, cue.volume ?? 0.6, cue.pitch ?? 1.0)
            }
          }
        }

        if (this.pathType === 'peekAtCamera') {
          // 観察中だけ、左右にコテッと傾きながら軽く首を振る（興味津々の仕草）
          const t = this.progress.t
          const peekWindow = THREE.MathUtils.smoothstep(t, 0.5, 0.6) * (1 - THREE.MathUtils.smoothstep(t, 0.78, 0.88))
          this.group.rotation.z = Math.sin(t * this.duration * 2.2 + this.wavePhase) * 0.18 * peekWindow
          this.group.rotation.y = Math.sin(t * this.duration * 1.4) * 0.12 * peekWindow
          return
        }

        if (this.pathType === 'crashIntoCamera') {
          // バランスを崩したクルクル回転（制御不能フェーズから加速し、落下中も回り続ける）
          const spinT = THREE.MathUtils.smoothstep(this.progress.t, 0.45, 0.85)
          this.modelPivot.rotation.set(spinT * 13.0, this.modelYaw + spinT * 8.0, spinT * 10.0)

          // 画面への衝突を1回だけ通知（reducedMotion 中は飛行が一瞬なので鳴らさない）
          if (!this.impactFired && this.progress.t >= IMPACT_T && !this.quality.reducedMotion) {
            this.impactFired = true
            this.onImpact?.(this.getScreenPosition())
          }
          return
        }

        // 機首は向けず、上を保ったまま上下の動きでバンクさせる
        const dy = this.group.position.y - this.prevPosition.y
        this.group.rotation.z = THREE.MathUtils.clamp(-dy * 8, -0.5, 0.5)
        // 円盤UFOの自転
        if (this.spin !== 0) {
          this.modelPivot.rotation.y = this.modelYaw + this.progress.t * this.duration * this.spin
        }
      },
      onComplete: () => {
        this.group.visible = false
        this.isFlying = false
      },
    })
  }
}
